import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel
} from "@/components/ui/select";
import { Upload, FileUp, Loader2, AlertTriangle } from "lucide-react";
import {
  getFoldersForKnowledgeBase,
  uploadDocumentToKnowledgeBase
} from "../../services/documentUploadService";
import { KnowledgeBaseWithDocuments, KnowledgeBaseFolder } from "../../../../types/navigationTypes";

interface FolderWithKB extends KnowledgeBaseFolder {
  knowledgeBaseName: string;
}

export interface UploadedDocument {
  id: string;
  name: string;
}

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  knowledgeBaseId?: string;
  initialFolderId?: string;
  initialFiles?: File[];
  knowledgeBases: KnowledgeBaseWithDocuments[];
  onUploadSuccess: (documents: UploadedDocument[]) => void;
}

const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  isOpen,
  onClose,
  knowledgeBaseId: initialKnowledgeBaseId,
  initialFolderId,
  initialFiles,
  knowledgeBases,
  onUploadSuccess
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [foldersWithKB, setFoldersWithKB] = useState<FolderWithKB[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{current: number, total: number}>({current: 0, total: 0});

  // Filtrar solo KBs asociadas a la ejecución (permiten subida)
  const filteredKnowledgeBases = useMemo(() => {
    return knowledgeBases.filter(
      (kb) =>
        kb.metadata?.is_execution_associated === true && kb.is_global !== true
    );
  }, [knowledgeBases]);

  useEffect(() => {
    if (isOpen && filteredKnowledgeBases.length > 0) {
      loadAllFolders();
    }
  }, [isOpen, filteredKnowledgeBases]);

  useEffect(() => {
    if (isOpen) {
      if (initialFiles) setFiles(initialFiles);
      if (initialFolderId) setSelectedFolderId(initialFolderId);
    } else {
      resetForm();
    }
  }, [isOpen, initialKnowledgeBaseId, initialFolderId, initialFiles, filteredKnowledgeBases]);

  const loadAllFolders = async () => {
    try {
      setLoadingFolders(true);
      
      const folderPromises = filteredKnowledgeBases.map(async (kb) => {
        try {
          const folders = await getFoldersForKnowledgeBase(kb.id);
          return folders.map(folder => ({
            ...folder,
            knowledgeBaseName: kb.name
          }));
        } catch (err) {
          console.error(`Error loading folders for KB ${kb.id}:`, err);
          return [];
        }
      });

      const foldersArrays = await Promise.all(folderPromises);
      const allFolders = foldersArrays.flat();
      
      setFoldersWithKB(allFolders);
      if (allFolders.length > 0 && !selectedFolderId && !initialFolderId) {
        setSelectedFolderId(allFolders[0].id);
      } else if (initialFolderId) {
        setSelectedFolderId(initialFolderId);
      }
    } catch (err) {
      console.error('Error loading folders:', err);
      setError('Error loading folders');
    } finally {
      setLoadingFolders(false);
    }
  };

  const resetForm = () => {
    setFiles([]);
    setSelectedFolderId('');
    setError(null);
    setUploadProgress({current: 0, total: 0});
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) {
      setFiles(prev => [...prev, ...selectedFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Select at least one file');
      return;
    }

    if (!selectedFolderId) {
      setError('Select a folder');
      return;
    }

    const selectedFolder = foldersWithKB.find(f => f.id === selectedFolderId);
    if (!selectedFolder) {
      setError('Selected folder not found');
      return;
    }

    const targetKnowledgeBaseId = selectedFolder.knowledge_base_id;
    const effectiveFolderId = selectedFolderId;

    try {
      setLoading(true);
      setError(null);
      setUploadProgress({current: 0, total: files.length});

      const uploadedDocs: UploadedDocument[] = [];
      
      for (let i = 0; i < files.length; i++) {
        setUploadProgress(prev => ({ ...prev, current: i + 1 }));
        const file = files[i];
        
        const result = await uploadDocumentToKnowledgeBase({
          file,
          knowledgeBaseId: targetKnowledgeBaseId,
          folderId: effectiveFolderId,
          documentName: file.name,
          description: undefined
        });

        if (result.success && result.documentId) {
          uploadedDocs.push({
            id: result.documentId,
            name: file.name
          });
        } else {
          throw new Error(result.error || `Error uploading ${file.name}`);
        }
      }

      resetForm();
      onUploadSuccess(uploadedDocs);
      onClose();
    } catch (err) {
      console.error('Error uploading documents:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px] max-w-[95vw] overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4 min-w-0">
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {error}
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-2">
            <Label htmlFor="file" className="flex items-center gap-1">
              Files <span className="text-red-500">*</span>
            </Label>
            <Input
              id="file"
              type="file"
              multiple
              onChange={handleFileChange}
              disabled={loading}
              className="cursor-pointer file:cursor-pointer"
            />
            {files.length > 0 && (
              <div className="flex flex-col gap-2 mt-2 min-w-0">
                <Label className="text-xs text-muted-foreground">Selected files ({files.length}):</Label>
                <div className="max-h-[120px] overflow-y-auto border rounded-md p-2 bg-muted/20 overflow-x-hidden">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between py-1 text-xs min-w-0 gap-2">
                      <span className="truncate flex-1">{f.name}</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-muted-foreground">{(f.size / 1024 / 1024).toFixed(2)} MB</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => removeFile(i)}
                          disabled={loading}
                        >
                          ×
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid gap-2 min-w-0">
            <Label className="flex items-center gap-1">
              Folder <span className="text-red-500">*</span>
            </Label>
            
            {loadingFolders ? (
              <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading folders...
              </div>
            ) : (
              <Select
                value={selectedFolderId}
                onValueChange={setSelectedFolderId}
                disabled={loading}
              >
                <SelectTrigger className="w-full h-9 overflow-hidden">
                  <SelectValue placeholder="Select a folder" />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-2rem)] sm:max-w-[450px]">
                  {filteredKnowledgeBases.map(kb => {
                    const kbFolders = foldersWithKB.filter(f => f.knowledge_base_id === kb.id);
                    if (kbFolders.length === 0) return null;
                    
                    return (
                      <SelectGroup key={kb.id}>
                        <SelectLabel className="truncate">{kb.name} ({kbFolders.length})</SelectLabel>
                        {kbFolders.map(folder => (
                          <SelectItem key={folder.id} value={folder.id} className="max-w-full">
                            <span className="truncate block">{folder.name}</span>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={loading || files.length === 0 || !selectedFolderId}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading {uploadProgress.current}/{uploadProgress.total}...
              </>
            ) : (
              <>
                <FileUp className="mr-2 h-4 w-4" />
                Upload Documents
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentUploadModal;
